// DisplayPathPanel.js
//
// Right-side slide-in panel that hosts the path-planning inputs the user
// needs to configure before kicking off a path calculation. In the old
// layout these inputs were buried in the top-right overlay alongside the
// scale/gsd readouts; that conflated "what am I looking at" info with
// "what am I about to do" inputs. Separating them makes the planning flow
// discoverable — click Display Path on the primary toolbar, the panel
// slides in, fill it out, hit Apply.
//
// The panel is a thin shell around the same controlled inputs the old
// overlay used. We reuse ImageEditor's state (turningRadius, preStraight,
// workspaceBuffer, navigationRowSpacing) and its change handlers, so the
// downstream Display Path computation (currently triggered from
// ToolbarComponent's handleDisplayPath) sees identical values regardless
// of which UI the user drove them from.
//
// Apply just proxies to the parent's onApply callback; the actual
// computation lives in ToolbarComponent's handleDisplayPath for now. A
// later phase will pull that logic up into ImageEditor so the old
// ToolbarComponent can be deleted entirely.

import React from 'react';
import { X, Play } from 'lucide-react';

const panelStyle = {
  position: 'fixed',
  top: '68px',
  right: '12px',
  width: '300px',
  maxHeight: 'calc(100vh - 96px)',
  overflowY: 'auto',
  backgroundColor: 'white',
  border: '1px solid #cfd7d0',
  borderRadius: '10px',
  boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
  padding: '14px 16px 16px',
  zIndex: 1150,
  fontFamily: 'Arial, sans-serif',
  fontSize: '0.85rem',
};

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '12px',
};

const titleStyle = {
  margin: 0,
  fontSize: '1rem',
  fontWeight: 700,
  color: '#223',
};

const closeBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  padding: 0,
  border: 'none',
  borderRadius: '4px',
  backgroundColor: 'transparent',
  color: '#556',
  cursor: 'pointer',
};

const rowStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 88px',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '8px',
};

const labelStyle = {
  fontSize: '0.82rem',
  color: '#334',
  fontWeight: 500,
};

const unitStyle = {
  fontSize: '0.72rem',
  color: '#889',
  fontWeight: 400,
  marginLeft: '4px',
};

const inputStyle = {
  width: '100%',
  padding: '4px 6px',
  border: '1px solid #cfd7d0',
  borderRadius: '5px',
  fontSize: '0.85rem',
  textAlign: 'right',
  boxSizing: 'border-box',
};

// A control whose VALUE text is long cannot live in the narrow value column —
// the turn-type select's longest option is "Reeds-Shepp (fwd + reverse)", which
// rendered clipped inside 72px. Full-width rows drop the label onto its own line
// and hand the control the panel's entire width, so option text stays readable
// no matter how long it gets.
const wideRowStyle = {
  display: 'block',
  marginBottom: '8px',
};

const selectStyle = {
  ...inputStyle,
  // inputStyle right-aligns, which suits numbers. A select shows prose, and
  // native selects render the arrow on the right, so left-align keeps the
  // option text away from it.
  textAlign: 'left',
  marginTop: '4px',
};

// The panel grew from 5 rows to 10, and those rows do three different jobs:
// turn SHAPE, GENERATION-time transitions baked into the pickle, and field
// LAYOUT. Flat, they read as one undifferentiated wall of numbers, so the
// operator cannot tell which ones force a regeneration.
const sectionStyle = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: '8px',
  margin: '14px 0 6px',
  paddingBottom: '4px',
  borderBottom: '1px solid #eef2ee',
};

const sectionTitleStyle = {
  fontSize: '0.68rem',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#6b7a6b',
};

const sectionNoteStyle = {
  fontSize: '0.68rem',
  fontWeight: 400,
  color: '#98a598',
  whiteSpace: 'nowrap',
};

const applyBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  width: '100%',
  marginTop: '8px',
  padding: '10px 12px',
  border: 'none',
  borderRadius: '6px',
  backgroundColor: '#2e7d32',
  color: 'white',
  fontWeight: 700,
  fontSize: '0.9rem',
  cursor: 'pointer',
};

const hintStyle = {
  marginTop: '10px',
  padding: '8px 10px',
  backgroundColor: '#f4f8f4',
  border: '1px solid #dfe8df',
  borderRadius: '6px',
  fontSize: '0.75rem',
  color: '#445',
  lineHeight: 1.4,
};

// Default every callback to a no-op so the panel never throws on a
// keystroke when the parent forgets a handler. The panel is driven
// entirely by props, so missing a callback just means "changes don't
// propagate" — annoying, but not a crash mid-typing. Same rationale
// for onApply / onClose (buttons go silent rather than throwing).
const noop = () => {};

// `first` suppresses the top margin so the leading section sits tight under the
// panel header instead of opening a double gap.
const Section = ({ title, note, first = false }) => (
  <div style={first ? { ...sectionStyle, marginTop: 0 } : sectionStyle}>
    <span style={sectionTitleStyle}>{title}</span>
    {note ? <span style={sectionNoteStyle}>{note}</span> : null}
  </div>
);
const DisplayPathPanel = ({
  isOpen,
  onClose = noop,
  turningRadius,
  onTurningRadiusChange = noop,
  preStraightDistance,
  abLoop,
  abLoopCount,
  onAbLoopCountChange,
  minSptDistance,
  onMinSptDistanceChange,
  minSpsDistance,
  onMinSpsDistanceChange,
  turnType,
  onTurnTypeChange,
  cuspStraightenDistance,
  onCuspStraightenDistanceChange,
  onPreStraightDistanceChange = noop,
  workspaceBuffer,
  onWorkspaceBufferChange = noop,
  innerBoundaryDistanceFeet,
  onInnerBoundaryDistanceChange = noop,
  navigationRowSpacing,
  onNavigationRowSpacingChange = noop,
  isCalculating = false,
  calculateProgress = 0,
  onApply = noop,
  hasLines = true,
}) => {
  if (!isOpen) return null;

  const rawNumber = (event, prev, fallback) => {
    const v = event.target.value;
    // allow transient empty string while typing
    if (v === '') return prev;
    const n = parseFloat(v);
    return isNaN(n) ? (prev !== null ? prev : fallback) : n;
  };

  return (
    <aside aria-label="Display path settings" style={panelStyle}>
      <div style={headerStyle}>
        <h3 style={titleStyle}>Display Path</h3>
        <button type="button" onClick={onClose} style={closeBtnStyle} title="Close panel">
          <X size={16} strokeWidth={2} />
        </button>
      </div>

      <Section title="Turn geometry" first />

      <div style={rowStyle}>
        <label style={labelStyle}>
          Turning radius
          <span style={unitStyle}>m</span>
        </label>
        <input
          type="number"
          step="0.1"
          value={turningRadius ?? 4.5}
          onChange={(e) => onTurningRadiusChange(rawNumber(e, turningRadius, 4.5))}
          style={inputStyle}
        />
      </div>

      <div style={rowStyle}>
        <label style={labelStyle}>
          {/* This value is the POST-turn straight (path_execute.get_dubins_override_path:
              `post_turn_distance = pre_turn_distance`) — the segment that straightens the
              robot after the curve. The prop/API name stays `preStraightDistance` so the
              backend contract is unchanged; only the operator-facing label is corrected.
              Units are METERS: it flows to normal_operation_start_only(pre_turn_distance=)
              which works in the UTM metric frame. */}
          Post-turn distance
          <span style={unitStyle}>m</span>
        </label>
        <input
          type="number"
          step="0.1"
          value={preStraightDistance ?? 5}
          onChange={(e) => onPreStraightDistanceChange(rawNumber(e, preStraightDistance, 5))}
          style={inputStyle}
        />
      </div>

      {/* Only meaningful when AB loop is enabled (toggle lives in the utility menu,
          which is checkbox-only and cannot host a number field). Shown disabled rather
          than hidden so the setting is discoverable before the toggle is flipped. */}
      <div style={rowStyle}>
        <label style={{ ...labelStyle, opacity: abLoop ? 1 : 0.45 }}>
          AB loops
          <span style={unitStyle}>x</span>
        </label>
        <input
          type="number"
          step="1"
          min="1"
          max="100"
          disabled={!abLoop}
          value={abLoopCount ?? 1}
          onChange={(e) => onAbLoopCountChange(Math.max(1, Math.min(100, rawNumber(e, abLoopCount, 1))))}
          style={{ ...inputStyle, opacity: abLoop ? 1 : 0.45 }}
        />
      </div>

      {/* Which generator builds the cross-row turns. Blank leaves it to otto.yaml
          (mission_planner.dubins_override / reeds_shepp_override); picking one overrides
          it for THIS generation only. Dubins is forward-only, so it needs more headland
          room; Reeds-Shepp can reverse and fits tighter, at the cost of cusps.
          Full-width row: the option text is far too long for the value column. */}
      <div style={wideRowStyle}>
        <label style={labelStyle}>Turn type</label>
        <select
          value={turnType ?? ''}
          onChange={(e) => onTurnTypeChange(e.target.value)}
          style={selectStyle}
        >
          <option value="">otto.yaml</option>
          <option value="dubins">Dubins (forward only)</option>
          <option value="reeds_shepp">Reeds-Shepp (fwd + reverse)</option>
        </select>
      </div>

      {/* ── Transition geometry ───────────────────────────────────────────────
          All GENERATION-time: baked into the path when it is built, not read by
          the robot at runtime, so changing one means re-generating. Left blank
          they fall back to otto.yaml, which is the previous behaviour — the
          section note says so once rather than repeating it per row. */}
      <Section title="Transitions" note="blank = otto.yaml" />

      {/* SPT and SPS gate DIFFERENT things and are set independently:
            SPT = straight BEFORE the curve, where the toolbar RAISES.
            SPS = straight before re-entering the row, where it LOWERS and the robot
                  accelerates back to planting speed.
          Either left blank falls back to the shared otto.yaml value
          (path_tracker.transitions.min_spt_sps_distance). Both are floors: the effective
          distance is max(this, physics), so a value below the physics figure changes
          nothing. Too short a value yields a 1-waypoint degenerate segment, which
          crashes Spline2D. */}
      <div style={rowStyle}>
        <label style={labelStyle}>
          Min SPT
          <span style={unitStyle}>m</span>
        </label>
        <input
          type="number" step="0.1" min="0" placeholder="otto.yaml"
          value={minSptDistance ?? ''}
          onChange={(e) => onMinSptDistanceChange(e.target.value === '' ? null : rawNumber(e, minSptDistance, 3))}
          style={inputStyle}
        />
      </div>

      <div style={rowStyle}>
        <label style={labelStyle}>
          Min SPS
          <span style={unitStyle}>m</span>
        </label>
        <input
          type="number" step="0.1" min="0" placeholder="otto.yaml"
          value={minSpsDistance ?? ''}
          onChange={(e) => onMinSpsDistanceChange(e.target.value === '' ? null : rawNumber(e, minSpsDistance, 3))}
          style={inputStyle}
        />
      </div>

      <div style={rowStyle}>
        <label style={labelStyle}>
          {/* Inserts a short straight at each Reeds-Shepp forward<->reverse cusp so the
              wheels relax toward centre before the next arc. 0 = off. Field experience
              put the useful value around 3 m. */}
          Cusp straighten
          <span style={unitStyle}>m</span>
        </label>
        <input
          type="number"
          step="0.1"
          min="0"
          placeholder="otto.yaml"
          value={cuspStraightenDistance ?? ''}
          onChange={(e) => onCuspStraightenDistanceChange(e.target.value === '' ? null : rawNumber(e, cuspStraightenDistance, 0))}
          style={inputStyle}
        />
      </div>

      <Section title="Field layout" />

      <div style={rowStyle}>
        <label style={labelStyle}>
          Workspace buffer
          <span style={unitStyle}>m</span>
        </label>
        <input
          type="number"
          step="0.1"
          value={workspaceBuffer ?? 1}
          onChange={(e) => onWorkspaceBufferChange(rawNumber(e, workspaceBuffer, 1))}
          style={inputStyle}
        />
      </div>

      <div style={rowStyle}>
        <label style={labelStyle}>
          Headland width
          <span style={unitStyle}>ft</span>
        </label>
        <input
          type="number"
          step="0.5"
          value={innerBoundaryDistanceFeet ?? 5}
          onChange={(e) => onInnerBoundaryDistanceChange(rawNumber(e, innerBoundaryDistanceFeet, 5))}
          style={inputStyle}
        />
      </div>

      <div style={rowStyle}>
        <label style={labelStyle}>
          Nav row spacing
          <span style={unitStyle}>cm</span>
        </label>
        <input
          type="number"
          step="0.1"
          value={navigationRowSpacing ?? 3}
          onChange={(e) => onNavigationRowSpacingChange(rawNumber(e, navigationRowSpacing, 3))}
          style={inputStyle}
        />
      </div>

      <button
        type="button"
        onClick={onApply}
        disabled={!hasLines || isCalculating}
        style={{
          ...applyBtnStyle,
          backgroundColor: (!hasLines || isCalculating) ? '#9fbfa1' : '#2e7d32',
          cursor: (!hasLines || isCalculating) ? 'default' : 'pointer',
        }}
      >
        <Play size={14} strokeWidth={2} />
        {isCalculating
          ? `Calculating… ${Math.round(calculateProgress)}%`
          : 'Apply path'}
      </button>

      {!hasLines && (
        <div style={hintStyle}>
          Draw at least one line before applying a path.
        </div>
      )}
    </aside>
  );
};

export default DisplayPathPanel;
